/*
 * Module for the KB Role details modal.
 */

define([
  "backbone",
  "marionette",
  "messaging/messenger",
  "jquery",
  "underscore",
  "./kbRoleDetailsView",
  "./basicKbRoleInfoDisplay/basicKbRoleInfoModule",
  "./contentAndLearnerDisplay/contentAndLearnerModule",
  "./authoringRulesDisplay/authoringRulesModule",
  "./touchpointMapDisplay/touchpointMapModule",
  "./localeMapDisplay/localeMapModule",
  "modules/accountAdmin/kbRoleAdmin/kbRoleHistory/kbRoleHistoryModalModule",
], function (
  Backbone,
  Marionette,
  Messenger,
  $,
  _,
  KbRoleDetailsView,
  BasicKbRoleInfoModule,
  ContentAndLearnerModule,
  AuthoringRulesModule,
  TouchpointMapModule,
  LocaleMapModule,
  KbRoleHistoryModalModule,
) {
  "use strict";

  var KbRoleDetailsModalController = Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "kbRoleDetails";
      this.tenant = options.tenant;
      this._setUpVents();
    },
    show: function (options) {
      var kbRole;
      if (options.role) {
        kbRole = options.role;
        if (kbRole.isNew()) {
          Backbone.history.navigate("accountAdmin/tenants/" + this.tenant.id + "/roles/new");
        } else {
          Backbone.history.navigate(
            "accountAdmin/tenants/" + this.tenant.id + "/roles/" + kbRole.id,
          );
        }
      } else if (options.roleId) {
        var kbRoles = Messenger.reqres.request("tenant:kbRoles", this.tenant.id);
        var kbRoleId = options.roleId;
        if (kbRoleId == "new") {
          kbRoles.onSynced(function () {
            var kbRole = kbRoles.addNewKbRole();
            kbRole.set("tenantId", this.tenant.id);
            this.show({ role: kbRole });
          }, this);
          return;
        }
        kbRoleId = +kbRoleId;
        if (isNaN(kbRoleId) || kbRoleId === 0) {
          Backbone.history.navigate("accountAdmin/tenants/" + this.tenant.id + "/roles", {
            trigger: true,
          });
        } else {
          kbRoles.onSynced(function () {
            var model = kbRoles.get(kbRoleId);
            if (!model) {
              Backbone.history.navigate("accountAdmin/tenants/" + this.tenant.id + "/roles", {
                trigger: true,
              });
            } else {
              this.show({ role: model });
            }
          }, this);
        }
        return;
      } else {
        return;
      }
      this.layout = new KbRoleDetailsView.kbRoleDetailsModalLayout({ model: kbRole });

      //set up sub modules
      this.basicKbRoleInfoModule = this.addSubModule(
        new BasicKbRoleInfoModule({ region: this.layout.basicKbRoleInfoDiv }),
      );
      this.contentAndLearnerModule = this.addSubModule(
        new ContentAndLearnerModule({ region: this.layout.contentAndLearnerDiv }),
      );
      this.authoringRulesModule = this.addSubModule(
        new AuthoringRulesModule({ region: this.layout.authoringRulesDiv }),
      );
      this.touchpointMapModule = this.addSubModule(
        new TouchpointMapModule({ region: this.layout.touchpointMapDiv }),
      );
      this.localeMapModule = this.addSubModule(
        new LocaleMapModule({ region: this.layout.localeMapDiv }),
      );

      //get the tenant KBs
      var tenantId = Messenger.reqres.request("active:tenantId");
      this.tenantKbs = Messenger.reqres.request("tenant:kbs", tenantId);

      Messenger.onSave(function () {
        if (!this.kbRole) {
          Messenger.saveAttempted(true); //success
          return;
        }

        //Save the model, and only close the modal if the save is successful.
        //Otherwise, display appropriate error messages and allow the user to
        //correct their error.
        if (!Messenger.reqres.request("save:textbox")) {
          Messenger.saveAttempted(false);
        }
        // Since the KB Role will set a Last Update Date if something has actually changed,
        // must carefully determine if the role or any of its privileges have changed.
        // Only save the KB Role with its privileges if it is new or actually changed.
        var privileges = []
          .concat(this.contentAndLearnerModule.getPrivileges())
          .concat(this.authoringRulesModule.getPrivileges())
          .concat(this.touchpointMapModule.getPrivileges())
          .concat(this.localeMapModule.getPrivileges());
        var oldPrivilegeIds = {};
        var oldPrivilegeIdCount = 0;
        var haveNewPrivilege = false;
        kbRole.privileges().each(function (privilege) {
          oldPrivilegeIds[privilege.get("id")] = true;
          ++oldPrivilegeIdCount;
        });
        _.each(privileges, function (privilege) {
          if (privilege.isNew()) {
            haveNewPrivilege = true;
          } else if (oldPrivilegeIds[privilege.get("id")]) {
            oldPrivilegeIds[privilege.get("id")] = false;
            --oldPrivilegeIdCount;
          }
        });
        if (haveNewPrivilege || oldPrivilegeIdCount > 0) {
          kbRole.privileges().reset(privileges);
          kbRole.dirty = true;
        }
        if (kbRole.isNew() || kbRole.dirty) {
          var currentUser = Messenger.reqres.request("user");
          kbRole.set("lastUpdateDate", new Date());
          kbRole.set("lastUpdateUserId", currentUser.get("userId"));
          kbRole.set("lastUpdateUserName", currentUser.get("userName"));
          kbRole.saveModel({
            error: function (model, response) {
              if (response.status === 400) {
                // noinspection JSUnresolvedVariable
                if (response.responseJSON.value === "The KB Role name must be unique") {
                  Messenger.errorMsg = $.t("admin.kbRoleAdmin.uniqueName");
                  Messenger.saveAttempted(false);
                  return;
                }
              }
              Messenger.setErrorMsgFromResponse(response);
              Messenger.saveAttempted(false);
            },
          });
          return;
        }
        Messenger.saveAttempted(true);
      }, this);

      this.kbRole = kbRole;
      this.layout.on("show", this._showSubViews, this);
      this.layout.on("delete:kbRole", this.onDeleteKbRole, this);

      this.showRegion(this.layout);
    },
    onDeleteKbRole: function () {
      var model = this.kbRole;
      Messenger.yesNoDialog(
        $.t("admin.kbRoleAdmin.confirmDeleteKbRole", { name: model.get("name") }),
        {
          yes: function () {
            Messenger.vent.trigger("track:interaction", {
              action: "Delete KB Role",
              id: model.id,
              idType: "KB Role",
              object: "KB Role",
              source: "KB Role Details",
            });
            Messenger.vent.trigger("cancel:textbox");
            model.deleteModel({
              success: function () {
                Messenger.vent.trigger("show:tenant:profile:roles");
              },
            });
          },
        },
        this,
      );
    },
    onKbIdChanged: function (kbId) {
      const kb = this.tenantKbs.get(kbId);
      if (kbId != this.kbRole.get("kbId")) {
        this.kbRole.set("kbId", kbId);
        if (kb) {
          this.kbRole.set("kbName", kb.get("kbName"));
        } else {
          this.kbRole.set("kbName", kbId);
        }
        this.kbRole.privileges().reset([]);
      }
      this.contentAndLearnerModule.show(this.kbRole, kb);
      this.authoringRulesModule.show(this.kbRole);
      this.touchpointMapModule.show(this.kbRole);
      this.localeMapModule.show(this.kbRole);
    },
    onKbRoleHistory: function () {
      Messenger.vent.trigger(
        "show:modal",
        KbRoleHistoryModalModule,
        {
          editMode: false,
        },
        this.kbRole,
        {
          title: $.t("admin.kbRoleAdmin.history"),
          closeButton: true,
          closeOnBackgroundClick: false,
          modalWidth: "900px",
          trackInteraction: {
            action: "KB Role History",
            id: this.kbRole.id,
            idType: "KB Role",
            object: "KB Role",
          },
        },
      );
    },
    _showSubViews: function () {
      var kbRole = this.kbRole;
      kbRole.onSynced(function () {
        this.tenantKbs.onSynced(function () {
          var kbId = kbRole.get("kbId");
          if (!kbId) {
            kbId = this.tenantKbs.at(0).get("kbId");
          }
          this.onKbIdChanged(kbId);
          this.basicKbRoleInfoModule.show(kbRole, this.tenantKbs);
        }, this);
      }, this);
    },
    _setUpVents: function () {
      Messenger.vent.on("kbRole:kbId:changed", this.onKbIdChanged, this);
      Messenger.vent.on("kbRole:history", this.onKbRoleHistory, this);
      Messenger.vent.on("show:tenant:profile:role", this.show, this);
    },
  });

  return KbRoleDetailsModalController;
});
